import React from "react";
import axios from "axios";
import { Loader2, RefreshCw, Search, X } from "lucide-react";
import { MatchDialog, STATUS_CLS } from "@/components/GreenBallMatch";
import { GreenBallAudit, GreenBallSources } from "@/components/GreenBallAudit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tennis`;
const TABS = [["center", "Match Center"], ["card", "Official Card"], ["audit", "Results & Audit"], ["sources", "Data Sources"]];
const fmtDay = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

const norm = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
// every word typed must appear in the match's players, countries, tournament, round, court, location or surface
export const matchHit = (m, q, t = {}) => {
  const words = norm(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = norm([...(m.players || []).flatMap((p) => [p.name, p.short, p.country]), m.tournament, t.name, m.round, m.court,
    m.location, t.location, m.surface, t.surface, m.tour].join(" "));
  return words.every((w) => hay.includes(w));
};

const SearchBar = ({ q, setQ, shown, total }) => (
  <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3" data-testid="gb-search">
    <Search className="h-4 w-4 shrink-0 text-slate-500" />
    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player, country, tournament, round or surface"
      aria-label="Search matches" data-testid="gb-search-input"
      className="h-10 min-w-0 flex-1 bg-transparent font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none" />
    {q && <span className="shrink-0 font-mono text-[10px] text-slate-500" data-testid="gb-search-count">{shown}/{total}</span>}
    {q && <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid="gb-search-clear" className="rounded p-1 text-slate-500 hover:text-lime-300"><X className="h-4 w-4" /></button>}
  </div>
);

const Player = ({ p, pick }) => (
  <div className={`flex items-center gap-1.5 ${p.winner ? "font-bold text-lime-200" : "text-slate-200"}`}>
    {p.flag && <img src={p.flag} alt="" className="h-3 w-4 rounded-[2px] object-cover" />}
    <span className="truncate">{p.name}</span>{p.seed && <span className="text-[9px] text-slate-500">({p.seed})</span>}
    {pick && <span className="rounded bg-lime-400/15 px-1 text-[8px] font-bold text-lime-300">PICK</span>}
    <span className="ml-auto font-mono text-[11px] text-slate-400">{p.sets.map((s) => s.g).join(" ")}</span>
  </div>
);

const MatchRow = ({ m, onOpen }) => (
  <button type="button" onClick={() => onOpen(m.id)} data-testid={`gb-match-${m.id}`}
    className="grid w-full grid-cols-[1fr_auto] items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-left transition-colors hover:border-lime-700/60">
    <div className="min-w-0 space-y-0.5 text-[12px]">
      <p className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{m.round} · {m.state === "pre" ? time(m.date) : m.status}{m.court ? ` · ${m.court}` : ""}</p>
      {m.players.map((p) => <Player key={p.id} p={p} pick={m.eval.pick_id === p.id && m.state !== "post"} />)}
    </div>
    <div className="text-right font-mono">
      <span className={`block rounded px-1.5 py-0.5 text-[9px] font-bold ${STATUS_CLS[m.eval.status] || "bg-slate-800 text-slate-500"}`}>{m.eval.status}</span>
      {m.eval.result && <span className={`block text-[9px] font-bold ${m.eval.result === "WIN" ? "text-lime-300" : m.eval.result === "LOSS" ? "text-rose-400" : "text-slate-500"}`} data-testid={`gb-result-${m.id}`}>{m.eval.result}</span>}
      <span className="text-[11px] text-lime-200">{Math.round(m.eval.prob * 100)}%</span>
      <span className="block text-[9px] text-slate-500">TG {m.eval.totals.projection ?? "—"}</span>
    </div>
  </button>
);

const Center = ({ data, tour, q, setQ, onOpen }) => {
  const groups = data.tournaments.filter((t) => tour === "ALL" || t.tour === tour)
    .map((t) => ({ ...t, hits: t.matches.filter((m) => matchHit(m, q, t)) }));
  const total = groups.reduce((n, t) => n + t.matches.length, 0);
  const shown = groups.reduce((n, t) => n + t.hits.length, 0);
  return (
    <div className="space-y-4">
      {data.count > 0 && <SearchBar q={q} setQ={setQ} shown={shown} total={total} />}
      {groups.filter((t) => t.hits.length).map((t) => (
        <section key={t.name} data-testid={`gb-tournament-${t.name.replace(/\W+/g, "-").toLowerCase()}`}>
          <p className="mb-1.5 font-display text-[13px] font-bold uppercase tracking-wide text-lime-200">{t.tour} · {t.name} <span className="font-mono text-[10px] font-normal text-slate-500">· {t.surface} · {t.location}</span></p>
          <div className="grid gap-2 md:grid-cols-2">{t.hits.map((m) => <MatchRow key={m.id} m={m} onOpen={onOpen} />)}</div>
        </section>
      ))}
      {!data.count && <p className="py-8 text-center font-mono text-[11px] text-slate-500" data-testid="gb-empty">No ATP / WTA singles matches on this day.</p>}
      {data.count > 0 && !shown && <p className="py-8 text-center font-mono text-[11px] text-slate-500" data-testid="gb-search-empty">No matches for “{q}” on this day.</p>}
    </div>
  );
};

const Card = ({ date, tour, q, setQ, onOpen }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    setD(null);
    axios.get(`${API}/card`, { params: { date } }).then((r) => live && setD(r.data)).catch(() => live && setD({ error: true }));
    return () => { live = false; };
  }, [date]);
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-lime-400" />;
  if (d.error) return <p className="font-mono text-xs text-rose-400" data-testid="gb-card-error">Official Card unavailable.</p>;
  const tourRows = d.rows.filter((m) => tour === "ALL" || m.tour === tour);
  const rows = tourRows.filter((m) => matchHit(m, q));
  return (
    <div className="space-y-2" data-testid="gb-card">
      <div className="flex flex-wrap gap-2 font-mono text-[10px]">
        {Object.entries(d.counts).map(([k, v]) => <span key={k} className={`rounded px-2 py-1 font-bold ${STATUS_CLS[k]}`} data-testid={`gb-card-count-${k.replace(/\W+/g, "-").toLowerCase()}`}>{k} {v}</span>)}
      </div>
      <p className="font-mono text-[10px] text-amber-300/80">{d.rule}</p>
      {tourRows.length > 0 && <SearchBar q={q} setQ={setQ} shown={rows.length} total={tourRows.length} />}
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full font-mono text-[11px]">
          <thead className="bg-slate-900 text-[9px] uppercase text-slate-500"><tr>{["Status", "Pick", "Prob", "Match", "Event", "Risk", "TG proj", "H2H"].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
          <tbody>{rows.map((m) => (
            <tr key={m.id} onClick={() => onOpen(m.id)} className="cursor-pointer border-t border-slate-800/60 hover:bg-lime-950/20" data-testid={`gb-card-row-${m.id}`}>
              <td className="px-2 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${STATUS_CLS[m.eval.status]}`}>{m.eval.status}</span></td>
              <td className="whitespace-nowrap px-2 py-1.5 font-bold text-lime-200">{m.eval.pick}</td>
              <td className="px-2 py-1.5">{Math.round(m.eval.prob * 100)}%</td>
              <td className="whitespace-nowrap px-2 py-1.5 text-slate-300">{m.players.map((p) => p.short).join(" vs ")}</td>
              <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">{m.tour} · {m.round}</td>
              <td className="px-2 py-1.5 text-slate-400">{m.eval.risk}</td>
              <td className="px-2 py-1.5 text-slate-300">{m.eval.totals.projection ?? "—"}</td>
              <td className="px-2 py-1.5 text-slate-400">{m.eval.h2h.p1}-{m.eval.h2h.p2}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function GreenBallTab() {
  const [tab, setTab] = React.useState("center");
  const [date, setDate] = React.useState(fmtDay(new Date()));
  const [tour, setTour] = React.useState("ALL");
  const [q, setQ] = React.useState("");
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const latest = React.useRef(date);
  latest.current = date;
  const load = React.useCallback(() => {
    const keep = (v) => { if (latest.current === date) setData(v); };
    axios.get(`${API}/slate`, { params: { date: date.replace(/-/g, "") } }).then((r) => keep(r.data)).catch(() => keep({ error: true }));
  }, [date]);
  React.useEffect(() => { setData(null); load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  return (
    <div className="space-y-4" data-testid="green-ball">
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`gb-tab-${k}`}
            className={`min-h-[38px] rounded-lg px-3 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-lime-400/15 text-lime-300 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.45)]" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gb-date" className="h-9 rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-slate-200" />
          <div className="flex rounded-md border border-slate-800 bg-slate-900 p-0.5">
            {["ALL", "ATP", "WTA"].map((t) => <button key={t} onClick={() => setTour(t)} data-testid={`gb-tour-${t}`} className={`rounded px-2.5 py-1 font-mono text-[11px] font-bold ${tour === t ? "bg-lime-400/15 text-lime-300" : "text-slate-500"}`}>{t}</button>)}
          </div>
          <button onClick={load} data-testid="gb-refresh" className="rounded-md border border-slate-800 p-2 text-slate-400 hover:text-lime-300"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {data?.backfill_running && <p className="rounded-lg border border-amber-900/50 bg-amber-950/20 px-3 py-2 font-mono text-[10px] text-amber-300" data-testid="gb-building">Building 12-month results history ({data.history_matches} matches so far) — form, surface and H2H sharpen as it completes.</p>}
      {tab === "center" && (!data ? <div className="flex items-center gap-2 py-10 font-mono text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-lime-400" /> Loading ATP / WTA slate…</div>
        : data.error ? <p className="font-mono text-xs text-rose-400">Tennis slate unavailable. <button onClick={load} className="underline">Retry</button></p>
        : <Center data={data} tour={tour} q={q} setQ={setQ} onOpen={setOpen} />)}
      {tab === "card" && <Card date={date.replace(/-/g, "")} tour={tour} q={q} setQ={setQ} onOpen={setOpen} />}
      {tab === "audit" && <GreenBallAudit />}
      {tab === "sources" && <GreenBallSources />}
      <MatchDialog matchId={open} date={date.replace(/-/g, "")} onClose={() => setOpen(null)} />
    </div>
  );
}
